// Sunice Variant Product Card JavaScript
// Handles variant picker initialization and interactions for variant product cards

(function() {
  'use strict';

  /**
   * Fetch full product data from Shopify API
   * @param {string} productUrl - The product URL
   * @returns {Promise<any>} Product data
   */
  async function fetchProductData(productUrl) {
    try {
      // Extract product handle from URL
      const urlParts = productUrl.split('/products/');
      if (urlParts.length < 2) return null;
      
      const productHandle = urlParts[1].split('?')[0].split('#')[0];
      const response = await fetch(`/products/${productHandle}.js`);
      
      if (!response.ok) return null;
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching product data:', error);
      return null;
    }
  }

  /**
   * Initialize variant picker for a variant product card
   * Pre-selects the color and size for the displayed variant
   * @param {HTMLElement} picker
   * @param {number|string} variantId - The ID of the variant displayed on the card
   * @param {boolean} fetchFromAPI - Whether to fetch fresh data from API (default: false to prevent 429 errors)
   */
  async function initializeVariantCardPicker(picker, variantId, fetchFromAPI = false) {
    // First, try to get data from the JSON script in the template
    // Look in the picker first, then in the parent form, then in the parent card
    let variantsDataScript = picker.querySelector('.product-variants-data');
    if (!variantsDataScript) {
      const form = picker.closest('form');
      if (form) {
        variantsDataScript = form.querySelector('.product-variants-data');
      }
    }
    if (!variantsDataScript) {
      const card = picker.closest('.sunice-product-card');
      if (card) {
        variantsDataScript = card.querySelector('.product-variants-data');
      }
    }
    
    let productData = null;
    
    if (variantsDataScript instanceof HTMLScriptElement) {
      try {
        productData = JSON.parse(variantsDataScript.textContent || '{}');
      } catch (error) {
        console.error('Error parsing variant data:', error);
      }
    }

    // Only fetch from API if explicitly requested (when form is opened)
    // This prevents 429 errors from too many API calls on page load
    if (fetchFromAPI) {
      const productUrl = picker.dataset.productUrl;
      if (productUrl) {
        const fetchedProduct = await fetchProductData(productUrl);
        if (fetchedProduct && fetchedProduct.variants && fetchedProduct.variants.length > 0) {
          // Convert Shopify product format to our format
          productData = {
            variants: fetchedProduct.variants.map(v => ({
              id: v.id,
              options: v.options || [],
              available: v.available !== false
            })),
            options: fetchedProduct.options.map((opt, index) => ({
              name: opt.name || opt,
              position: index + 1,
              values: opt.values || []
            }))
          };
          
          // Update the JSON script with fresh data for future use
          if (variantsDataScript instanceof HTMLScriptElement) {
            variantsDataScript.textContent = JSON.stringify(productData);
          }
          
          // ALSO store on the picker element for direct access (faster than parsing JSON)
          picker.dataset.productData = JSON.stringify(productData);
        }
      }
    }

    if (!productData) {
      // Don't error - data might not be available yet, will initialize when form opens
      // This is normal for cards that are just being revealed through pagination
      return;
    }

    const variantsList = productData?.variants || [];
    const optionsList = productData?.options || [];

    // Find the variant that matches the displayed variant ID (handle both string and number)
    const variantIdNum = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;
    const displayedVariant = variantsList.find(v => v.id === variantIdNum || String(v.id) === String(variantIdNum));
    if (!displayedVariant) {
      console.warn('Variant not found for ID:', variantIdNum, 'Available variants:', variantsList.map(v => v.id));
      return;
    }

    // Find Color/Colour and Size options
    const colorOption = optionsList.find(opt => opt?.name === 'Color' || opt?.name === 'Colour');
    const sizeOption = optionsList.find(opt => opt?.name === 'Size');

    if (!colorOption || !sizeOption) {
      // Fallback: just sync the variant ID input
      syncVariantIdInput(picker, displayedVariant.id);
      return;
    }

    const colorOptionPosition = colorOption.position - 1;
    const sizeOptionPosition = sizeOption.position - 1;

    // Get the color and size values for the displayed variant
    const variantColor = displayedVariant.options?.[colorOptionPosition];
    const variantSize = displayedVariant.options?.[sizeOptionPosition];

    // Find all color fieldsets (Core and Seasonal) - color can be in either
    const colorFieldsets = Array.from(picker.querySelectorAll('fieldset')).filter(fs => {
      const legend = fs.querySelector('legend');
      return legend && (legend.textContent.trim() === 'Color' || legend.textContent.trim() === 'Colour' || legend.textContent.trim() === 'Core:' || legend.textContent.trim() === 'Seasonal:');
    });

    if (colorFieldsets.length > 0 && variantColor) {
      // Clear all color swatch active states first (Core + Seasonal)
      clearAllColorSwatchActiveStates(picker);
      const variantColorNorm = variantColor.toString().toLowerCase().trim();
      colorFieldsets.forEach(colorFieldset => {
        const colorRadios = Array.from(colorFieldset.querySelectorAll('input[type="radio"]'));
        colorRadios.forEach(radio => {
          if (!(radio instanceof HTMLInputElement)) return;
          const radioValue = radio.value.toString().toLowerCase().trim();
          if (radioValue === variantColorNorm) {
            radio.checked = true;
            const label = radio.closest('label');
            if (label instanceof HTMLElement) {
              label.classList.add('active');
              if (label.classList.contains('color-swatch')) {
                label.setAttribute('data-active', 'true');
              }
            }
          } else {
            radio.checked = false;
            const label = radio.closest('label');
            if (label instanceof HTMLElement) {
              label.classList.remove('active');
              label.removeAttribute('data-active');
            }
          }
        });
      });
    }

    // Find and select the size fieldset
    const sizeFieldset = picker.querySelector(`fieldset[data-option-id="option-${sizeOption.position}"]`) ||
                        Array.from(picker.querySelectorAll('fieldset')).find(fs => {
                          const legend = fs.querySelector('legend');
                          return legend && legend.textContent.trim() === 'Size';
                        });

    if (sizeFieldset instanceof HTMLFieldSetElement && variantSize) {
      const sizeRadios = Array.from(sizeFieldset.querySelectorAll('input[type="radio"]'));
      sizeRadios.forEach(radio => {
        if (!(radio instanceof HTMLInputElement)) return;
        const radioValue = radio.value.toString().toLowerCase().trim();
        const variantSizeValue = variantSize.toString().toLowerCase().trim();
        
        if (radioValue === variantSizeValue) {
          radio.checked = true;
          const label = radio.closest('label');
          if (label instanceof HTMLElement) {
            label.classList.add('active');
          }
        } else {
          radio.checked = false;
          const label = radio.closest('label');
          if (label instanceof HTMLElement) {
            label.classList.remove('active');
          }
        }
      });
    }

    // Select any remaining options (e.g. inseam) from the displayed variant
    optionsList.forEach((option) => {
      if (option.name === 'Color' || option.name === 'Colour' || option.name === 'Size') return;
      const position = option.position - 1;
      const optionValue = displayedVariant.options?.[position];
      if (!optionValue) return;

      const fieldset =
        picker.querySelector(`fieldset[data-option-id="option-${option.position}"]`) ||
        Array.from(picker.querySelectorAll('fieldset')).find((fs) => {
          const legend = fs.querySelector('legend');
          return legend && legend.textContent.trim().startsWith(option.name);
        });

      if (!(fieldset instanceof HTMLFieldSetElement)) return;

      const targetNorm = optionValue.toString().toLowerCase().trim();
      fieldset.querySelectorAll('input[type="radio"]').forEach((radio) => {
        if (!(radio instanceof HTMLInputElement)) return;
        const isMatch = radio.value.toString().toLowerCase().trim() === targetNorm;
        radio.checked = isMatch;
        const label = radio.closest('label');
        if (label instanceof HTMLElement) {
          label.classList.toggle('active', isMatch);
        }
      });
    });

    // Sync the variant ID input
    syncVariantIdInput(picker, displayedVariant.id);
    
    // FIRST: Set initial availability for ALL options based on product variants
    // This shows which options are available/unavailable before any selection
    setInitialAvailabilityForVariantCard(picker, variantsList, optionsList);
    
    // THEN: Update availability states based on current selections
    updateUnavailableStatesForVariantCard(picker);
    
    // FINALLY: Update availability based on selected color (this updates sizes based on color)
    if (variantColor) {
      requestAnimationFrame(() => {
        updateAvailabilityForColorSelectionForVariantCard(picker, variantColor);
        // Reorganize seasonal swatches after availability updates
        reorganizeSeasonalSwatchesForPicker(picker);
        updateProductCardLegends(picker);
      });
    }
    
    // Reorganize seasonal swatches after variant update
    reorganizeSeasonalSwatchesForPicker(picker);
    updateProductCardLegends(picker);
  }

  /**
   * Set initial availability for all options based on product variants
   * This ensures all variants are visible with proper availability styling
   */
  function setInitialAvailabilityForVariantCard(picker, variantsList, optionsList) {
    // For each option, check which values have available variants
    optionsList.forEach(option => {
      const optionPosition = option.position - 1;
      
      // Find the fieldset for this option
      let fieldset = picker.querySelector(`fieldset[data-option-id="option-${option.position}"]`);
      if (!fieldset) {
        const allFieldsets = Array.from(picker.querySelectorAll('fieldset'));
        fieldset = allFieldsets.find(fs => {
          const legend = fs.querySelector('legend');
          const legendText = legend?.textContent?.trim();
          if (option.name === 'Color' || option.name === 'Colour') {
            return legendText === 'Core:' || legendText === 'Color:' || legendText === 'Colour:';
          }
          return legendText === option.name + ':';
        });
      }
      if (!fieldset) return;
      
      // Get all labels for this option
      let labels = Array.from(fieldset.querySelectorAll('label.sunice-variant-option__button-label'));
      if (labels.length === 0) {
        labels = Array.from(fieldset.querySelectorAll('label'));
      }
      
      labels.forEach(label => {
        const radio = label.querySelector('input[type="radio"]');
        if (!(radio instanceof HTMLInputElement)) return;
        
        const optionValue = radio.value;
        const normalizeValue = (val) => {
          if (!val) return '';
          return val.toString().toLowerCase().trim().replace(/\\\//g, '/');
        };
        const normalizedOptionValue = normalizeValue(optionValue);
        
        // Check if there's ANY variant (available or not) with this option value
        const hasAnyVariant = variantsList.some(variant => {
          const variantOptionValue = variant.options?.[optionPosition];
          const normalizedVariantOption = normalizeValue(variantOptionValue);
          return normalizedVariantOption === normalizedOptionValue;
        });
        
        // Check if there's an AVAILABLE variant with this option value
        const hasAvailableVariant = variantsList.some(variant => {
          const variantOptionValue = variant.options?.[optionPosition];
          const normalizedVariantOption = normalizeValue(variantOptionValue);
          return normalizedVariantOption === normalizedOptionValue && variant.available;
        });
        
        // Set availability attributes (sizes only - colors handled above)
        if (hasAvailableVariant) {
          label.classList.remove('unavailable');
          label.classList.remove('variant-option__button-label--out-of-stock');
          label.removeAttribute('data-unavailable');
          label.setAttribute('data-option-available', 'true');
          radio.setAttribute('data-option-available', 'true');
          radio.disabled = false;
        } else if (hasAnyVariant) {
          if (!radio.checked) {
            label.classList.add('unavailable');
            label.classList.add('variant-option__button-label--out-of-stock');
            label.setAttribute('data-unavailable', 'true');
          }
          label.setAttribute('data-option-available', 'false');
          radio.setAttribute('data-option-available', 'false');
          radio.disabled = false;
        } else {
          if (!radio.checked) {
            label.classList.add('unavailable');
            label.classList.add('variant-option__button-label--out-of-stock');
            label.setAttribute('data-unavailable', 'true');
          }
          label.setAttribute('data-option-available', 'false');
          radio.setAttribute('data-option-available', 'false');
          radio.disabled = false;
        }
      });
    });
  }

  /**
   * Sync the variant ID input with the selected options
   * @param {HTMLElement} picker
   * @param {number} variantId - Optional variant ID to set directly
   */
  function syncVariantIdInput(picker, variantId) {
    const form = picker.closest('form');
    if (!form) return;

    const variantIdInput = form.querySelector('input[name="id"]');
    if (!(variantIdInput instanceof HTMLInputElement)) return;

    if (variantId) {
      variantIdInput.value = String(variantId);
      return;
    }

    // If no variant ID provided, calculate from selected options
    // Try to get from picker dataset first (faster), then fall back to script tag
    let productData = null;
    
    if (picker.dataset.productData) {
      try {
        productData = JSON.parse(picker.dataset.productData);
      } catch (error) {
        console.error('Error parsing product data from dataset:', error);
      }
    }
    
    // Fallback to script tag if dataset doesn't have it
    // Try multiple locations: picker, form, card, document
    if (!productData) {
      let variantsDataScript = picker.querySelector('.product-variants-data');
      
      if (!variantsDataScript && form) {
        variantsDataScript = form.querySelector('.product-variants-data');
      }
      
      if (!variantsDataScript) {
        const productCard = picker.closest('.sunice-product-card');
        if (productCard) {
          variantsDataScript = productCard.querySelector('.product-variants-data');
        }
      }
      
      // Last resort: search in the entire document for this product's data
      if (!variantsDataScript) {
        const productId = picker.dataset.productId || '';
        const allScripts = document.querySelectorAll('.product-variants-data');
        for (const script of allScripts) {
          if (script instanceof HTMLScriptElement) {
            try {
              const data = JSON.parse(script.textContent || '{}');
              if (data.productId && String(data.productId) === String(productId)) {
                variantsDataScript = script;
                break;
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
      
      if (variantsDataScript instanceof HTMLScriptElement) {
        try {
          productData = JSON.parse(variantsDataScript.textContent || '{}');
          console.log('✅ Found product data from script tag');
        } catch (error) {
          console.error('Error parsing product data from script tag:', error);
          console.error('Script content:', variantsDataScript.textContent?.substring(0, 200));
          return;
        }
      } else {
        console.error('❌ No product data available in syncVariantIdInput');
        console.error('Picker:', picker);
        console.error('Form:', form);
        console.error('Product ID:', picker.dataset.productId);
        console.error('All .product-variants-data scripts found:', document.querySelectorAll('.product-variants-data').length);
        return;
      }
    }

    const variantsList = productData?.variants || [];
    const optionsList = productData?.options || [];

    if (variantsList.length === 0 || optionsList.length === 0) {
      console.warn('No variants or options found in product data');
      return;
    }

    // Get all selected options - MUST get ALL of them
    const selectedOptions = {};
    const checkedRadios = picker.querySelectorAll('input[type="radio"]:checked');
    
    if (checkedRadios.length === 0) {
      console.warn('syncVariantIdInput: No options selected');
      return;
    }
    
    // Ensure we have a selection for each option in the optionsList
    optionsList.forEach((option) => {
      // Find the checked radio for this option
      const productId = picker.dataset.productId || '';
      const optionName = option.name;
      const radioName = `${optionName}-${productId}`;
      
      // Find checked radio with this name
      const radio = Array.from(checkedRadios).find((r) => {
        if (r instanceof HTMLInputElement) {
          return r.name === radioName;
        }
        return false;
      });
      
      if (radio instanceof HTMLInputElement) {
        selectedOptions[optionName] = radio.value;
      } else {
        // No selection for this option - this is a problem
        console.warn('syncVariantIdInput: No selection found for option:', optionName);
      }
    });
    
    console.log('🔍 syncVariantIdInput: Found', checkedRadios.length, 'checked radios');
    console.log('🔍 syncVariantIdInput: All selected options (raw):', selectedOptions);
    console.log('🔍 syncVariantIdInput: Available options:', optionsList.map(o => ({ name: o.name, position: o.position })));
    
    // DEBUG: Log what each radio button value is
    checkedRadios.forEach((radio, idx) => {
      if (radio instanceof HTMLInputElement) {
        const productId = picker.dataset.productId || '';
        const optionName = radio.name.replace(`-${productId}`, '');
        console.log(`🔍 Radio ${idx}: name="${radio.name}", value="${radio.value}", optionName="${optionName}"`);
      }
    });

    // Ensure we have selections for ALL options
    // If not all options are selected, we can't find a matching variant, but that's okay
    // The user might be in the middle of selecting options
    if (Object.keys(selectedOptions).length !== optionsList.length) {
      console.warn('Not all options selected. Selected:', Object.keys(selectedOptions), 'Expected:', optionsList.map(o => o.name));
      // Don't return - continue to try to find a variant with what we have
    }

    // Normalize function for value comparison - handle various formats
    const normalizeValue = (val) => {
      if (!val) return '';
      // Convert to string, lowercase, trim, and handle special characters
      let normalized = val.toString().toLowerCase().trim();
      // Remove any extra whitespace
      normalized = normalized.replace(/\s+/g, ' ');
      // Handle forward slashes
      normalized = normalized.replace(/\\\//g, '/');
      return normalized;
    };

    // Find variant that matches ALL selected options - must match EVERY option
    let matchingVariant = null;
    
    // First, ensure we have a selection for EVERY option
    const missingOptions = optionsList.filter(option => !selectedOptions.hasOwnProperty(option.name));
    if (missingOptions.length > 0) {
      console.warn('syncVariantIdInput: Not all options are selected. Missing:', missingOptions.map(o => o.name));
      // Don't return - try to find a variant anyway, but log a warning
    }
    
    // Try to find matching variant
    console.log('syncVariantIdInput: Searching through', variantsList.length, 'variants');
    console.log('syncVariantIdInput: Selected options (raw):', selectedOptions);
    
    for (const variant of variantsList) {
      // Check that variant has options array with correct length
      if (!variant.options || !Array.isArray(variant.options) || variant.options.length !== optionsList.length) {
        console.log('syncVariantIdInput: Skipping variant', variant.id, '- options length mismatch');
        continue;
      }
      
      // Check that EVERY option matches
      // CRITICAL: Use option.position - 1 to map to variant.options array index
      // This matches how the regular product card does it
      let matches = true;
      const matchDetails = [];
      
      for (const option of optionsList) {
        const selectedValue = selectedOptions[option.name];
        
        // If this option wasn't selected, skip it (but this shouldn't happen)
        if (!selectedValue) {
          matches = false;
          matchDetails.push(`Missing selection for ${option.name}`);
          break;
        }
        
        // Use option.position - 1 to get the correct index in variant.options
        // Shopify option positions are 1-based, but arrays are 0-based
        const optionPosition = option.position - 1;
        const variantValue = variant.options?.[optionPosition];
        
        const expectedValue = normalizeValue(selectedValue);
        const normalizedVariantValue = normalizeValue(variantValue);
        
        // CRITICAL DEBUG: Log the exact comparison
        console.log(`🔍 Comparing option "${option.name}" (position ${option.position}):`);
        console.log(`  Selected (raw): "${selectedValue}" (type: ${typeof selectedValue})`);
        console.log(`  Selected (normalized): "${expectedValue}"`);
        console.log(`  Variant (raw): "${variantValue}" (type: ${typeof variantValue})`);
        console.log(`  Variant (normalized): "${normalizedVariantValue}"`);
        console.log(`  Match: ${normalizedVariantValue === expectedValue ? '✅' : '❌'}`);
        
        matchDetails.push({
          option: option.name,
          optionPosition: option.position,
          arrayIndex: optionPosition,
          selectedRaw: selectedValue,
          selectedNormalized: expectedValue,
          variantRaw: variantValue,
          variantNormalized: normalizedVariantValue,
          match: normalizedVariantValue === expectedValue
        });
        
        if (normalizedVariantValue !== expectedValue) {
          matches = false;
          console.error(`❌ MISMATCH for option "${option.name}": "${selectedValue}" !== "${variantValue}"`);
          // Don't break - continue to log all mismatches for debugging
        } else {
          console.log(`✅ MATCH for option "${option.name}"`);
        }
      }
      
      if (matches) {
        matchingVariant = variant;
        console.log('✅ syncVariantIdInput: Found matching variant:', {
          id: variant.id,
          options: variant.options,
          selectedOptions: selectedOptions,
          matchDetails: matchDetails
        });
        break;
      } else {
        console.log('❌ syncVariantIdInput: Variant', variant.id, 'does not match:', matchDetails);
      }
    }

    // Update variant ID input
    if (matchingVariant) {
      const newVariantId = String(matchingVariant.id);
      const oldVariantId = variantIdInput.value;
      variantIdInput.value = newVariantId;
      
      console.log('✅ syncVariantIdInput: Updated variant ID from', oldVariantId, 'to', newVariantId, 'for options:', selectedOptions);
      console.log('✅ syncVariantIdInput: Variant details:', { id: matchingVariant.id, options: matchingVariant.options, available: matchingVariant.available });
      
      // CRITICAL: Dispatch variant:selected event immediately so cart drawer and other components know about the change
      const variantSelectedEvent = new CustomEvent('variant:selected', {
        detail: {
          id: matchingVariant.id,
          variant: matchingVariant
        },
        bubbles: true
      });
      picker.dispatchEvent(variantSelectedEvent);
      form.dispatchEvent(variantSelectedEvent);
      
      // Also dispatch on product card
      const productCard = picker.closest('.sunice-product-card');
      if (productCard) {
        productCard.dispatchEvent(new CustomEvent('variant:selected', {
          detail: {
            id: matchingVariant.id,
            variant: matchingVariant
          },
          bubbles: true
        }));
      }
      
      return true; // Success
    } else {
      // Normalize function for error logging (same as above)
      const normalizeForLog = (val) => {
        if (!val) return '';
        let normalized = val.toString().toLowerCase().trim();
        normalized = normalized.replace(/\s+/g, ' ');
        normalized = normalized.replace(/\\\//g, '/');
        return normalized;
      };
      
      console.error('❌ syncVariantIdInput: No matching variant found!');
      console.error('Selected options (raw):', selectedOptions);
      console.error('Selected options (normalized):', Object.keys(selectedOptions).reduce((acc, key) => {
        acc[key] = normalizeForLog(selectedOptions[key]);
        return acc;
      }, {}));
      console.error('Available variants:', variantsList.map(v => ({ 
        id: v.id, 
        options: v.options,
        optionsNormalized: v.options.map(opt => normalizeForLog(opt)),
        available: v.available 
      })));
      console.error('Options list:', optionsList);
      return false; // Failed
    }
  }

  /**
   * Clears active state from ALL color swatches (Core + Seasonal).
   * When a Core swatch is clicked, Seasonal active must be removed and vice versa.
   * @param {HTMLElement} picker
   */
  function clearAllColorSwatchActiveStates(picker) {
    if (!(picker instanceof HTMLElement)) return;
    picker.querySelectorAll('label.color-swatch').forEach((swatch) => {
      swatch.classList.remove('active');
      swatch.removeAttribute('data-active');
    });
  }

  /**
   * Handle variant option changes
   * @param {Event} event
   * @param {HTMLElement} picker
   */
  function handleVariantChange(event, picker) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;

    const label = target.closest('label');
    const fieldset = target.closest('fieldset');

    // Update active states: for color swatches, clear ALL (Core + Seasonal)
    if (fieldset instanceof HTMLFieldSetElement && label instanceof HTMLElement) {
      if (label.classList.contains('color-swatch')) {
        clearAllColorSwatchActiveStates(picker);
      } else {
        const allLabels = fieldset.querySelectorAll('label');
        allLabels.forEach(lbl => {
          if (lbl instanceof HTMLElement) {
            lbl.classList.remove('active');
            lbl.removeAttribute('data-active');
          }
        });
      }
      const allRadios = fieldset.querySelectorAll('input[type="radio"]');
      allRadios.forEach(radio => {
        if (radio instanceof HTMLInputElement && radio !== target) {
          radio.checked = false;
        }
      });
      label.classList.add('active');
      if (label.classList.contains('color-swatch')) {
        label.setAttribute('data-active', 'true');
      }
    }

    // Update availability states FIRST - this shows/hides options based on selection
    updateUnavailableStatesForVariantCard(picker);
    
    // Update availability based on what was selected
    const productId = picker.dataset.productId || '';
    const optionName = target.name.replace(`-${productId}`, '');
    const isColorOption = optionName === 'Color' || optionName === 'Colour';
    
    if (isColorOption) {
      // Update card imagery so hover uses the selected variant's secondary image
      const productCard = picker.closest('.sunice-product-card');
      if (productCard instanceof HTMLElement) {
        const primaryUrl = target.dataset.imageUrl || '';
        let secondaryUrl = target.dataset.secondaryImageUrl || '';

        // Prefer "next image in product ordering" after the selected variant's primary image
        try {
          const raw = productCard.dataset.productImages;
          if (raw) {
            /** @type {string[]} */
            const ordered = JSON.parse(raw);
            const normalize = (url) => {
              if (!url) return '';
              const withoutHost = url.replace(/^https?:\/\/[^/]+/i, '');
              return withoutHost.split('?')[0].split('#')[0];
            };
            const primaryKey = normalize(primaryUrl);
            const idx = ordered.findIndex((u) => normalize(u) === primaryKey);
            if (idx >= 0 && ordered.length > 1) {
              // Find the next DIFFERENT image after the primary (skip duplicates)
              for (let i = idx + 1; i < ordered.length; i++) {
                const candidate = ordered[i];
                const candKey = normalize(candidate);
                const looksLikeAnotherPrimary =
                  /_a(\.|$)/i.test(candKey) && !/_a(\.|$)/i.test(primaryKey);
                if (candKey && candKey !== primaryKey && !looksLikeAnotherPrimary) {
                  secondaryUrl = candidate || secondaryUrl;
                  break;
                }
              }
            }
          }
        } catch (e) {
          // ignore and fall back
        }

        /** @type {any} */
        const gw = window;
        if (typeof gw.updateProductCardPrimaryImage === 'function') {
          gw.updateProductCardPrimaryImage(productCard, primaryUrl, secondaryUrl);
        } else {
          const primaryImg = productCard.querySelector('.product-image-primary');
          const secondaryImg = productCard.querySelector('.product-image-secondary');
          if (primaryImg instanceof HTMLImageElement && primaryUrl) {
            primaryImg.src = primaryUrl;
            primaryImg.srcset = primaryUrl;
          }
          if (secondaryImg instanceof HTMLImageElement && secondaryUrl) {
            secondaryImg.src = secondaryUrl;
            secondaryImg.srcset = secondaryUrl;
          }
        }
      }

      // Update availability for color selection (updates sizes based on color)
      updateAvailabilityForColorSelectionForVariantCard(picker, target.value);
      // Reorganize seasonal swatches after color selection
      reorganizeSeasonalSwatchesForPicker(picker);
      updateProductCardLegends(picker);
    } else {
      // Update availability for size selection (updates colors based on size)
      updateAvailabilityForSizeSelectionForVariantCard(picker, optionName, target.value);
      // Reorganize seasonal swatches after size selection
      reorganizeSeasonalSwatchesForPicker(picker);
      updateProductCardLegends(picker);
    }
    
    // CRITICAL: Sync variant ID input AFTER availability updates
    // This ensures the correct variant is selected when user clicks add to cart
    syncVariantIdInput(picker);
    
    // Update price when variant changes (use setTimeout to ensure variant ID is synced first)
    setTimeout(() => {
      updatePriceForVariantCard(picker);
    }, 0);
    
    // Verify variant ID was set correctly and dispatch variant:selected event
    const form = picker.closest('form');
    if (form) {
      const variantIdInput = form.querySelector('input[name="id"]');
      if (variantIdInput instanceof HTMLInputElement && variantIdInput.value) {
        const variantId = parseInt(variantIdInput.value, 10);
        console.log('Variant ID synced to:', variantId, 'after selecting:', target.value);
        
        // Get product data to find full variant object
        let productData = null;
        if (picker.dataset.productData) {
          try {
            productData = JSON.parse(picker.dataset.productData);
          } catch (error) {
            // Fallback to script tag
            const variantsDataScript = picker.querySelector('.product-variants-data');
            if (variantsDataScript instanceof HTMLScriptElement) {
              try {
                productData = JSON.parse(variantsDataScript.textContent || '{}');
              } catch (e) {
                console.error('Error parsing product data:', e);
              }
            }
          }
        }
        
        // Find the variant object
        const variantsList = productData?.variants || [];
        const variant = variantsList.find(v => v.id === variantId);
        
        // Get selected options for the event
        const selectedOptions = {};
        const checkedRadios = picker.querySelectorAll('input[type="radio"]:checked');
        checkedRadios.forEach((radio) => {
          if (radio instanceof HTMLInputElement) {
            const productId = picker.dataset.productId || '';
            const optionName = radio.name.replace(`-${productId}`, '');
            selectedOptions[optionName] = radio.value;
          }
        });
        
        // Dispatch variant-selected event with full details (like regular product card)
        picker.dispatchEvent(new CustomEvent('variant-selected', {
          detail: {
            variantId: variantId,
            variant: variant || { id: variantId },
            selectedOptions: selectedOptions
          },
          bubbles: true
        }));
        
        // Dispatch variant:selected event for theme compatibility
        const variantSelectedEvent = new CustomEvent('variant:selected', {
          detail: {
            id: variantId,
            variant: variant || { id: variantId }
          },
          bubbles: true
        });
        picker.dispatchEvent(variantSelectedEvent);
        form.dispatchEvent(variantSelectedEvent);
        
        // Also dispatch on the product card for broader compatibility (like regular product card)
        const productCard = picker.closest('.sunice-product-card');
        if (productCard) {
          productCard.dispatchEvent(new CustomEvent('variant:selected', {
            detail: {
              id: variantId,
              variant: variant || { id: variantId }
            },
            bubbles: true
          }));
        }
      }
    }
  }

  /**
   * Update price display when variant is selected
   * @param {HTMLElement} picker
   */
  function updatePriceForVariantCard(picker) {
    const productCard = picker.closest('.sunice-variant-product-card, .sunice-product-card[data-variant-id]');
    if (!productCard) return;

    const priceContainer = productCard.querySelector('.product-card__price-container');
    if (!priceContainer) return;

    // Get product data
    let productData = null;
    if (picker.dataset.productData) {
      try {
        productData = JSON.parse(picker.dataset.productData);
      } catch (error) {
        console.error('Error parsing product data:', error);
      }
    }
    
    if (!productData) {
      const variantsDataScript = picker.querySelector('.product-variants-data');
      if (variantsDataScript instanceof HTMLScriptElement) {
        try {
          productData = JSON.parse(variantsDataScript.textContent || '{}');
        } catch (error) {
          console.error('Error parsing variant data:', error);
          return;
        }
      } else {
        return;
      }
    }

    const variantsList = productData?.variants || [];
    if (variantsList.length === 0) return;

    // Get selected variant ID
    const form = picker.closest('form');
    if (!form) return;
    
    const variantIdInput = form.querySelector('input[name="id"]');
    if (!(variantIdInput instanceof HTMLInputElement) || !variantIdInput.value) return;

    const selectedVariantId = parseInt(variantIdInput.value, 10);
    const selectedVariant = variantsList.find(v => v.id === selectedVariantId);
    
    if (!selectedVariant || !selectedVariant.price) return;

    // Format price (price is in cents)
    const formattedPrice = formatPrice(selectedVariant.price);
    const formattedComparePrice = selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price 
      ? formatPrice(selectedVariant.compare_at_price) 
      : '';
    const isOnSale = selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price;

    // Update price display
    const priceElement = priceContainer.querySelector('.product-card__price');
    const comparePriceElement = priceContainer.querySelector('.product-card__compare-price');

    if (priceElement && formattedPrice) {
      priceElement.innerHTML = formattedPrice;
      
      if (isOnSale) {
        priceElement.classList.add('product-card__price--sale');
        priceElement.classList.remove('product-card__price--regular');
      } else {
        priceElement.classList.remove('product-card__price--sale');
        priceElement.classList.add('product-card__price--regular');
      }
    }

    // Update compare price
    if (isOnSale && formattedComparePrice) {
      if (comparePriceElement) {
        comparePriceElement.innerHTML = formattedComparePrice;
      } else {
        const newComparePrice = document.createElement('span');
        newComparePrice.className = 'product-card__compare-price';
        newComparePrice.innerHTML = formattedComparePrice;
        priceContainer.appendChild(newComparePrice);
      }
    } else {
      if (comparePriceElement) {
        comparePriceElement.remove();
      }
    }

    updateSaleBadgeForVariantCard(productCard, selectedVariant.compare_at_price, selectedVariant.price);
  }

  /**
   * Update sale badge on variant product card image
   * @param {HTMLElement} productCard
   * @param {number|null} compareAtPrice
   * @param {number} price
   */
  function updateSaleBadgeForVariantCard(productCard, compareAtPrice, price) {
    const imageContainer = productCard.querySelector('.product-card__image');
    if (!imageContainer) return;

    const compareCents = parseInt(String(compareAtPrice || '0'), 10);
    const priceCents = parseInt(String(price || '0'), 10);
    const isOnSale = compareCents > priceCents && priceCents > 0;
    let badge = imageContainer.querySelector('.product-card.sale-badge');

    if (isOnSale) {
      const salePercentage = Math.round(((compareCents - priceCents) * 100) / compareCents);
      const badgeText = salePercentage > 0 ? `${salePercentage}% off` : 'Sale';
      if (badge) {
        badge.textContent = badgeText;
      } else {
        badge = document.createElement('div');
        badge.className = 'product-card sale-badge';
        badge.textContent = badgeText;
        imageContainer.appendChild(badge);
      }
    } else if (badge) {
      badge.remove();
    }
  }

  function getActiveCurrency() {
    if (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) {
      return Shopify.currency.active;
    }
    return 'USD';
  }

  /**
   * Format price with superscript cents (matches Liquid formatting)
   * @param {number} priceInCents
   * @returns {string}
   */
  function formatPrice(priceInCents) {
    const price = priceInCents / 100;
    const currency = getActiveCurrency();
    const formatted = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency
    }).format(price);
    
    const testPrice = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency
    }).format(0);
    const currencySymbol = testPrice.replace(/[\d.,\s]/g, '').trim() || testPrice.replace('0.00', '');
    const amount = formatted.replace(currencySymbol, '').trim();
    
    if (!amount || amount === '') {
      return `<span class="currency-symbol">${currencySymbol}</span>${formatted.replace(currencySymbol, '').trim()}`;
    }
    
    const normalizedAmount = amount.replace(/,/g, '');
    const [dollars, cents] = normalizedAmount.split('.');
    const centsPart = cents || '00';
    
    if (centsPart === '00') {
      return `<span class="currency-symbol">${currencySymbol}</span>${dollars}`;
    }
    return `<span class="currency-symbol">${currencySymbol}</span>${dollars}<span class="cents">.${centsPart}</span>`;
  }

  /**
   * Setup variant picker for variant product cards
   */
  function setupVariantCardPickers() {
    // Find all variant product cards
    const variantCards = document.querySelectorAll('.sunice-product-card[data-variant-id]');
    
    variantCards.forEach((variantCard) => {
      const variantId = variantCard.getAttribute('data-variant-id');
      if (!variantId) return;

      const picker = variantCard.querySelector('.sunice-variant-picker');
      if (!picker || picker.hasAttribute('data-variant-card-initialized')) return;

      // Mark as initialized to prevent main script from initializing it
      picker.setAttribute('data-initialized', 'true');
      picker.setAttribute('data-variant-card-initialized', 'true');

      // Store the variant ID for later initialization
      picker.dataset.variantCardVariantId = variantId;
      
      // Initialize with template data only (no API call) to prevent 429 errors
      // API call will happen when form is opened via toggleProductForm
      initializeVariantCardPicker(picker, parseInt(variantId, 10), false);
      
      // Handle close button click
      const closeButton = picker.querySelector('.sunice-variant-picker__close');
      if (closeButton instanceof HTMLElement) {
        closeButton.addEventListener('click', function(e) {
          e.stopPropagation();
          const globalWindow = /** @type {any} */ (window);
          if (typeof globalWindow.closeProductForm === 'function') {
            // Create a temporary button element to pass to closeProductForm
            const tempButton = document.createElement('button');
            tempButton.classList.add('close-form-button');
            variantCard.appendChild(tempButton);
            globalWindow.closeProductForm(tempButton);
            tempButton.remove();
          }
        });
      }
      
      // Initialize native swatch carousel + keep variant title in sync with swatch clicks
      const globalWindow = /** @type {any} */ (window);
      if (globalWindow.SuniceSwatchCarousel && typeof globalWindow.SuniceSwatchCarousel.init === 'function') {
        globalWindow.SuniceSwatchCarousel.init(variantCard);
      }
      
      /**
       * Updates the variant product card link URL with the selected variant ID
       * @param {HTMLElement} picker - The variant picker element
       * @param {HTMLInputElement} radio - The selected radio button
       */
      function updateVariantProductCardLinkUrl(picker, radio) {
        if (!picker || !radio) return;
        
        // Find the product card
        const productCard = picker.closest('.sunice-variant-product-card, .sunice-product-card[data-variant-id]');
        if (!productCard) return;
        
        // Get variant ID from radio button data attribute or from the form's hidden input
        let variantId = radio.dataset.variantId || '';
        
        // If no variant ID on radio, try to get from the form's hidden input
        if (!variantId) {
          const form = picker.closest('form');
          if (form) {
            const variantIdInput = form.querySelector('input[name="id"]');
            if (variantIdInput instanceof HTMLInputElement) {
              variantId = variantIdInput.value;
            }
          }
        }
        
        // If still no variant ID, try to get from product variants data
        if (!variantId) {
          const variantsDataScript = picker.querySelector('.product-variants-data');
          if (variantsDataScript instanceof HTMLScriptElement) {
            try {
              const productData = JSON.parse(variantsDataScript.textContent || '{}');
              const variantsList = productData?.variants || [];
              
              // Find variant that matches all selected options
              const selectedOptions = {};
              const allRadios = picker.querySelectorAll('input[type="radio"]:checked');
              allRadios.forEach((r) => {
                if (r instanceof HTMLInputElement) {
                  const optionName = r.name.replace(`-${picker.dataset.productId}`, '').replace('-swatch', '');
                  selectedOptions[optionName] = r.value;
                }
              });
              
              // Find matching variant
              const matchingVariant = variantsList.find((v) => {
                if (!v.options || !Array.isArray(v.options)) return false;
                const optionsList = productData?.options || [];
                return optionsList.every((opt, idx) => {
                  const optionName = opt.name;
                  return v.options[idx] === selectedOptions[optionName];
                });
              });
              
              if (matchingVariant && matchingVariant.id) {
                variantId = String(matchingVariant.id);
              }
            } catch (e) {
              console.warn('Error parsing variant data:', e);
            }
          }
        }
        
        if (!variantId) return;
        
        // Find all product card links
        const productCardLink = productCard.querySelector('a[href*="/products/"]');
        const productTitleLink = productCard.querySelector('.product-card__title a, .product-title a');
        const cardGalleryLink = productCard.querySelector('.product-card__gallery-link');
        
        // Get base product URL (remove existing variant param)
        const baseUrl = picker.dataset.productUrl || (productCardLink ? productCardLink.href.split('?')[0] : '');
        if (!baseUrl) return;
        
        const url = new URL(baseUrl, window.location.origin);
        url.searchParams.set('variant', variantId);
        
        const newUrl = url.toString();
        
        // Update all product card links
        if (productCardLink instanceof HTMLAnchorElement) {
          productCardLink.href = newUrl;
        }
        if (productTitleLink instanceof HTMLAnchorElement) {
          productTitleLink.href = newUrl;
        }
        if (cardGalleryLink instanceof HTMLAnchorElement) {
          cardGalleryLink.href = newUrl;
        }
      }

        const variantTitle = variantCard.querySelector('.variant-title');
      if (variantTitle instanceof HTMLElement) {
        // Set initial title if possible
        const active = variantCard.querySelector('.color-swatch[data-active="true"], .color-swatch[aria-pressed="true"]');
        if (active instanceof HTMLElement) {
          const t = active.getAttribute('data-color') || active.getAttribute('title') || '';
          if (t) variantTitle.textContent = t;
        }

        // Update on click (delegated)
        variantCard.addEventListener('click', (e) => {
          const target = /** @type {any} */ (e).target;
          const swatch = target && target.closest ? target.closest('.color-swatch') : null;
          if (!(swatch instanceof HTMLElement)) return;
          const t = swatch.getAttribute('data-color') || swatch.getAttribute('title') || '';
          if (t) variantTitle.textContent = t;
        });
      }

      // Setup change event listeners - this is the primary handler
      // Use both capture and bubble phases to ensure we catch the event
      picker.addEventListener('change', function(event) {
        const target = event.target;
        if (target instanceof HTMLInputElement && target.type === 'radio') {
          console.log('Change event fired for radio:', target.value);
          handleVariantChange(event, picker);
        }
      }, false); // Use bubble phase (default) - capture might interfere with label behavior
      
      // Also listen for form submit to ensure variant ID is synced before submission
      const form = variantCard.querySelector('form.product-form');
      if (form) {
        // Use capture phase to run BEFORE handleShopifyAddToCart
        form.addEventListener('submit', function(event) {
          // CRITICAL: Sync variant ID immediately before form submission (synchronously)
          // This MUST happen synchronously to ensure the correct variant is used
          const syncSuccess = syncVariantIdInput(picker);
          
          // Verify variant ID is set and correct
          const variantIdInput = form.querySelector('input[name="id"]');
          if (!(variantIdInput instanceof HTMLInputElement)) {
            event.preventDefault();
            alert('Form error: variant ID input not found.');
            return false;
          }
          
          // Get current selections to verify
          const checkedRadios = picker.querySelectorAll('input[type="radio"]:checked');
          const selectedOptions = {};
          checkedRadios.forEach((radio) => {
            if (radio instanceof HTMLInputElement) {
              const productId = picker.dataset.productId || '';
              const optionName = radio.name.replace(`-${productId}`, '');
              selectedOptions[optionName] = radio.value;
            }
          });
          
          // Verify we have all required options selected
          let productData = null;
          const variantsDataScript = picker.querySelector('.product-variants-data');
          if (variantsDataScript instanceof HTMLScriptElement) {
            try {
              productData = JSON.parse(variantsDataScript.textContent || '{}');
            } catch (e) {
              console.error('Error parsing product data:', e);
            }
          }
          
          const optionsList = productData?.options || [];
          const missingOptions = optionsList.filter(opt => !selectedOptions.hasOwnProperty(opt.name));
          
          if (missingOptions.length > 0) {
            event.preventDefault();
            alert('Please select all required options before adding to cart.');
            return false;
          }
          
          if (!variantIdInput.value) {
            event.preventDefault();
            alert('Please select all required options before adding to cart.');
            return false;
          }
          
          // CRITICAL: Double-check the variant ID matches the selected options
          // If it doesn't match, sync again and verify
          const variantsList = productData?.variants || [];
          const currentVariantId = variantIdInput.value;
          const matchingVariant = variantsList.find(v => String(v.id) === String(currentVariantId));
          
          if (matchingVariant) {
            // Verify the variant options match the selected options
            let optionsMatch = true;
            const normalize = (val) => val ? val.toString().toLowerCase().trim().replace(/\s+/g, ' ') : '';
            
            for (let i = 0; i < optionsList.length; i++) {
              const option = optionsList[i];
              const selectedValue = selectedOptions[option.name];
              const variantValue = matchingVariant.options[i];
              
              if (normalize(selectedValue) !== normalize(variantValue)) {
                optionsMatch = false;
                console.warn('⚠️ Option mismatch:', option.name, 'Selected:', selectedValue, 'Variant has:', variantValue);
                break;
              }
            }
            
            if (!optionsMatch) {
              // Options don't match - sync again
              console.warn('⚠️ Variant options don\'t match selected options, syncing again...');
              const retrySync = syncVariantIdInput(picker);
              if (!retrySync) {
                event.preventDefault();
                alert('Unable to find matching variant. Please try selecting options again.');
                return false;
              }
              // Verify the new variant ID
              const newVariantId = form.querySelector('input[name="id"]')?.value;
              console.log('✅ After re-sync, variant ID:', newVariantId);
            }
          } else if (!syncSuccess) {
            // No matching variant found and sync failed
            event.preventDefault();
            alert('Unable to find matching variant. Please try selecting options again.');
            return false;
          }
          
          // Log for debugging - this will show what's actually being submitted
          console.log('✅ Form submitting with variant ID:', variantIdInput.value);
          console.log('✅ Selected options:', selectedOptions);
        }, { capture: true, passive: false });
      }

      // Setup click event listeners for labels
      // We need to manually check the radio and trigger change event to ensure variant ID syncs
      picker.addEventListener('click', function(event) {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;

        const label = target.closest('label.sunice-variant-option__button-label');
        if (label instanceof HTMLElement) {
          const radio = label.querySelector('input[type="radio"]');
          if (radio instanceof HTMLInputElement && !radio.disabled) {
            // Remove the !radio.checked condition - we want to handle all clicks
            // Manually check the radio (label click should do this, but be explicit)
            const wasChecked = radio.checked;
            radio.checked = true;
            
            // Update active states: for color swatches, clear ALL (Core + Seasonal)
            const fieldset = label.closest('fieldset');
            if (fieldset instanceof HTMLFieldSetElement) {
              if (label.classList.contains('color-swatch')) {
                clearAllColorSwatchActiveStates(picker);
              } else {
                const allLabels = fieldset.querySelectorAll('label');
                allLabels.forEach(l => {
                  if (l instanceof HTMLElement) {
                    l.classList.remove('active');
                    l.removeAttribute('data-active');
                  }
                });
              }
              const allRadios = fieldset.querySelectorAll('input[type="radio"]');
              allRadios.forEach(r => {
                if (r instanceof HTMLInputElement && r !== radio) {
                  r.checked = false;
                }
              });
              label.classList.add('active');
              if (label.classList.contains('color-swatch')) {
                label.setAttribute('data-active', 'true');
              }
            }
            
            // CRITICAL: Sync variant ID IMMEDIATELY after radio is checked (synchronously)
            // This ensures the correct variant is selected before any async operations
            const syncSuccess = syncVariantIdInput(picker);
            
            // Update product card link URL with selected variant
            updateVariantProductCardLinkUrl(picker, radio);
            
            if (!syncSuccess) {
              console.warn('⚠️ Failed to sync variant ID on click, will retry on change event');
            }
            
            // CRITICAL: Manually trigger change event to ensure handleVariantChange is called
            // This ensures variant ID is synced immediately and availability is updated
            // Only dispatch if it wasn't already checked (to avoid duplicate events)
            if (!wasChecked) {
              const changeEvent = new Event('change', { bubbles: true, cancelable: true });
              radio.dispatchEvent(changeEvent);
            } else {
              // If already checked, still call handleVariantChange directly to ensure sync
              // This handles the case where user clicks the same option again
              handleVariantChange({ target: radio }, picker);
            }
          }
        }
      });

      // Initialize when form is opened (if toggleProductForm is called)
      const selectButton = variantCard.querySelector('.select-size-button');
      if (selectButton instanceof HTMLElement) {
        // Remove any existing listeners to avoid duplicates
        const newSelectButton = selectButton.cloneNode(true);
        selectButton.parentNode.replaceChild(newSelectButton, selectButton);
        
        newSelectButton.addEventListener('click', async function() {
          // Re-initialize to ensure correct selection when form opens
          setTimeout(async () => {
            const storedVariantId = picker.dataset.variantCardVariantId || variantId;
            await initializeVariantCardPicker(picker, parseInt(storedVariantId, 10));
          }, 150);
        });
      }
      
      // Also listen for when the card becomes active (form opens)
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const target = mutation.target;
            if (target instanceof HTMLElement && target.classList.contains('active')) {
              // Form just opened, fetch product data and re-initialize
              setTimeout(async () => {
                const storedVariantId = picker.dataset.variantCardVariantId || variantId;
                await initializeVariantCardPicker(picker, parseInt(storedVariantId, 10));
              }, 100);
            }
          }
        });
      });
      
      observer.observe(variantCard, { attributes: true, attributeFilter: ['class'] });
    });
  }

  // Debounced initialization to prevent multiple rapid calls
  let initTimeout = null;
  const debouncedInitialize = () => {
    if (initTimeout) clearTimeout(initTimeout);
    initTimeout = setTimeout(() => {
      setupVariantCardPickers();
      initTimeout = null;
    }, 150); // Single debounced call instead of multiple timeouts
  };

  // Initialize when DOM is ready
  function initialize() {
    debouncedInitialize();
  }

  // Run immediately if DOM is ready, otherwise wait
  if (document.readyState === 'loading') {
    // Run as soon as DOM is ready, before other scripts
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    // DOM already ready, use requestIdleCallback for non-critical initialization
    if (window.requestIdleCallback) {
      requestIdleCallback(initialize, { timeout: 1000 });
    } else {
      // Fallback for older browsers
      setTimeout(initialize, 0);
    }
  }

  // Theme editor: only re-init on section:load (full section replace) and section:reorder
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:reorder', initialize);

  // Re-initialize when lazy-loaded sections load
  document.addEventListener('section:lazy-load', initialize);
  
  // Expose initialization functions globally for toggleProductForm and pagination
  window.initializeVariantCardPicker = initializeVariantCardPicker;
  window.setupVariantCardPickers = setupVariantCardPickers;
  
  // Wrap toggleProductForm to handle variant cards
  const originalToggleProductForm = window.toggleProductForm;
  if (typeof originalToggleProductForm === 'function') {
    window.toggleProductForm = function(button) {
      if (!(button instanceof HTMLElement)) return;
      
      const productCard = button.closest('.sunice-product-card');
      if (!(productCard instanceof HTMLElement)) return;
      
      // Check if this is a variant card BEFORE calling original function
      const isVariantCard = productCard.hasAttribute('data-variant-id');
      const variantId = isVariantCard ? productCard.getAttribute('data-variant-id') : null;
      
      // Call original function first
      const result = originalToggleProductForm.apply(this, arguments);
      
      // If it's a variant card, fetch full product data and initialize the picker
      if (isVariantCard && variantId) {
        const picker = productCard.querySelector('.sunice-variant-picker');
        if (picker) {
          // Wait a bit for the form to be visible, then fetch product data and initialize
          // Pass true to fetchFromAPI to get fresh data when form opens
          setTimeout(async () => {
            await initializeVariantCardPicker(picker, parseInt(variantId, 10), true);
          }, 100);
        }
      }
      
      return result;
    };
  }

  /**
   * Get product data from script tag (helper function)
   * @param {HTMLElement} picker
   * @returns {any|null}
   */
  function getProductDataForPicker(picker) {
    // Try dataset first (cached)
    if (picker.dataset.productData) {
      try {
        return JSON.parse(picker.dataset.productData);
      } catch (error) {
        console.error('Error parsing product data from dataset:', error);
      }
    }
    
    // Try multiple locations: picker, form, card, document
    let variantsDataScript = picker.querySelector('.product-variants-data');
    
    if (!variantsDataScript) {
      const form = picker.closest('form');
      if (form) {
        variantsDataScript = form.querySelector('.product-variants-data');
      }
    }
    
    if (!variantsDataScript) {
      const productCard = picker.closest('.sunice-product-card');
      if (productCard) {
        variantsDataScript = productCard.querySelector('.product-variants-data');
      }
    }
    
    // Last resort: search in the entire document for this product's data
    if (!variantsDataScript) {
      const productId = picker.dataset.productId || '';
      const allScripts = document.querySelectorAll('.product-variants-data');
      for (const script of allScripts) {
        if (script instanceof HTMLScriptElement) {
          try {
            const data = JSON.parse(script.textContent || '{}');
            if (data.productId && String(data.productId) === String(productId)) {
              variantsDataScript = script;
              break;
            }
          } catch (e) {
            // Skip invalid JSON
          }
        }
      }
    }
    
    if (variantsDataScript instanceof HTMLScriptElement) {
      try {
        return JSON.parse(variantsDataScript.textContent || '{}');
      } catch (error) {
        console.error('Error parsing product data from script tag:', error);
        return null;
      }
    }
    
    return null;
  }

  /**
   * Update availability for other options when a color is selected (for variant cards)
   * Based on updateAvailabilityForColorSelection from sunice-product-card.js
   */
  function updateAvailabilityForColorSelectionForVariantCard(picker, selectedColorValue) {
    const productData = getProductDataForPicker(picker);
    if (!productData) {
      console.warn('updateAvailabilityForColorSelectionForVariantCard: No product data found');
      return;
    }

    const variantsList = productData?.variants || [];
    const optionsList = productData?.options || [];
    
    const colorOption = optionsList.find(opt => opt?.name === 'Color' || opt?.name === 'Colour');
    if (!colorOption) return;
    
    const colorOptionPosition = colorOption.position - 1;
    
    const normalizeValue = (val) => {
      if (!val) return '';
      return val.toString().toLowerCase().trim().replace(/\\\//g, '/');
    };
    const normalizedSelectedColor = normalizeValue(selectedColorValue);
    
    const variantsWithThisColor = variantsList.filter(variant => {
      const variantColorValue = variant.options?.[colorOptionPosition];
      const normalizedVariantColor = normalizeValue(variantColorValue);
      return normalizedVariantColor === normalizedSelectedColor;
    });
    
    optionsList.forEach(option => {
      if (option.name === 'Color' || option.name === 'Colour') return;
      
      const optionPosition = option.position - 1;
      
      let fieldset = picker.querySelector(`fieldset[data-option-id="option-${option.position}"]`);
      if (!fieldset) {
        const allFieldsets = Array.from(picker.querySelectorAll('fieldset'));
        fieldset = allFieldsets.find(fs => {
          const legend = fs.querySelector('legend');
          return legend && legend.textContent.trim() === option.name;
        });
      }
      if (!fieldset) return;
      
      let labels = Array.from(fieldset.querySelectorAll('label.sunice-variant-option__button-label'));
      if (labels.length === 0) {
        labels = Array.from(fieldset.querySelectorAll('label'));
      }
      labels = labels.filter(label => !label.classList.contains('color-swatch'));
      
      labels.forEach(label => {
        const radio = label.querySelector('input[type="radio"]');
        if (!(radio instanceof HTMLInputElement)) return;
        
        const optionValue = radio.value;
        const normalizedOptionValue = normalizeValue(optionValue);
        
        const hasAvailableVariant = variantsWithThisColor.some(variant => {
          const variantOptionValue = variant.options?.[optionPosition];
          const normalizedVariantOption = normalizeValue(variantOptionValue);
          return normalizedVariantOption === normalizedOptionValue && variant.available;
        });
        
        const hasAnyVariant = variantsWithThisColor.some(variant => {
          const variantOptionValue = variant.options?.[optionPosition];
          const normalizedVariantOption = normalizeValue(variantOptionValue);
          return normalizedVariantOption === normalizedOptionValue;
        });
        
        if (hasAvailableVariant) {
          label.classList.remove('unavailable');
          label.classList.remove('variant-option__button-label--out-of-stock');
          label.removeAttribute('data-unavailable');
          label.setAttribute('data-option-available', 'true');
          radio.setAttribute('data-option-available', 'true');
          radio.disabled = false;
        } else if (hasAnyVariant) {
          if (!radio.checked) {
            label.classList.add('unavailable');
            label.classList.add('variant-option__button-label--out-of-stock');
            label.setAttribute('data-unavailable', 'true');
          }
          label.setAttribute('data-option-available', 'false');
          radio.setAttribute('data-option-available', 'false');
          radio.disabled = false;
        } else {
          if (!radio.checked) {
            label.classList.add('unavailable');
            label.classList.add('variant-option__button-label--out-of-stock');
            label.setAttribute('data-unavailable', 'true');
          }
          label.setAttribute('data-option-available', 'false');
          radio.setAttribute('data-option-available', 'false');
          radio.disabled = false;
        }
      });
    });

    // When color is clicked, select the first non-disabled (available) size
    const sizeOption = optionsList.find(opt => opt?.name === 'Size');
    if (sizeOption) {
      const sizeFieldset = picker.querySelector(`fieldset[data-option-id="option-${sizeOption.position}"]`) ||
        Array.from(picker.querySelectorAll('fieldset')).find(fs => {
          const legend = fs.querySelector('legend');
          return legend && legend.textContent.trim() === 'Size';
        });
      if (sizeFieldset instanceof HTMLFieldSetElement) {
        const checkedSizeRadio = sizeFieldset.querySelector('input[type="radio"]:checked');
        const availableSizeRadios = Array.from(sizeFieldset.querySelectorAll('input[type="radio"][data-option-available="true"]'));
        const firstAvailableSizeRadio = availableSizeRadios.length > 0 ? availableSizeRadios[0] : null;
        const shouldSelectFirstAvailable = firstAvailableSizeRadio && (
          !checkedSizeRadio ||
          !(checkedSizeRadio instanceof HTMLInputElement) ||
          checkedSizeRadio.dataset.optionAvailable === 'false' ||
          checkedSizeRadio.closest('label')?.classList.contains('unavailable')
        );
        if (shouldSelectFirstAvailable && firstAvailableSizeRadio instanceof HTMLInputElement) {
          const allLabels = sizeFieldset.querySelectorAll('label.sunice-variant-option__button-label');
          allLabels.forEach(lbl => {
            if (lbl instanceof HTMLElement) {
              lbl.classList.remove('active');
              lbl.removeAttribute('data-active');
            }
          });
          const allSizeRadios = Array.from(sizeFieldset.querySelectorAll('input[type="radio"]'));
          allSizeRadios.forEach(radio => {
            if (radio instanceof HTMLInputElement) radio.checked = false;
          });
          firstAvailableSizeRadio.checked = true;
          const sizeLabel = firstAvailableSizeRadio.closest('label.sunice-variant-option__button-label');
          if (sizeLabel instanceof HTMLElement) sizeLabel.classList.add('active');
          syncVariantIdInput(picker);
        }
      }
    }
  }

  /**
   * Update availability for colors when a size (or other non-color option) is selected (for variant cards).
   * Only mark a colour as unavailable if it has NO available variants in any size (fully unavailable).
   * Colours that have at least one size in stock must not get the unavailable class.
   */
  function updateAvailabilityForSizeSelectionForVariantCard(picker, selectedOptionName, selectedOptionValue) {
    const productData = getProductDataForPicker(picker);
    if (!productData) return;

    const variantsList = productData?.variants || [];
    const optionsList = productData?.options || [];
    const colorOption = optionsList.find(opt => opt?.name === 'Color' || opt?.name === 'Colour');
    if (!colorOption) return;

    const colorOptionPosition = colorOption.position - 1;
    const normalizeValue = (val) => {
      if (!val) return '';
      return val.toString().toLowerCase().trim().replace(/\\\//g, '/');
    };

    const colorFieldset = picker.querySelector(`fieldset[data-option-id="option-${colorOption.position}"]`);
    if (!colorFieldset) return;

    const colorLabels = Array.from(colorFieldset.querySelectorAll('label.sunice-variant-option__button-label.color-swatch'));
    colorLabels.forEach((label) => {
      const radio = label.querySelector('input[type="radio"]');
      if (!(radio instanceof HTMLInputElement)) return;

      const colorValue = radio.value;
      const normalizedColorValue = normalizeValue(colorValue);

      const hasAnyAvailableVariant = variantsList.some((variant) => {
        const variantColorValue = variant.options?.[colorOptionPosition];
        const normalizedVariantColor = normalizeValue(variantColorValue);
        return normalizedVariantColor === normalizedColorValue && variant.available;
      });

      if (hasAnyAvailableVariant) {
        label.classList.remove('unavailable');
        label.classList.remove('variant-option__button-label--out-of-stock');
        label.removeAttribute('data-unavailable');
        label.setAttribute('data-option-available', 'true');
        radio.setAttribute('data-option-available', 'true');
        radio.disabled = false;
        radio.removeAttribute('disabled');
      } else {
        label.classList.add('unavailable');
        label.classList.add('variant-option__button-label--out-of-stock');
        label.setAttribute('data-unavailable', 'true');
        label.setAttribute('data-option-available', 'false');
        radio.setAttribute('data-option-available', 'false');
      }
    });

    reorganizeSeasonalSwatchesForPicker(picker);
    updateProductCardLegends(picker);
  }

  /**
   * Update unavailable states for variant options based on current selections (for variant cards)
   * Based on updateUnavailableStates from sunice-product-card.js
   */
  function updateUnavailableStatesForVariantCard(picker) {
    // CRITICAL: Never modify facet swatches - they should stay consistent
    // Check if this picker is inside a facets component (desktop or mobile drawer)
    if (picker.closest('.facets__item, .facets__panel, .facets--drawer, facets-form-component')) {
      return; // Skip entirely if inside facets
    }
    
    const productData = getProductDataForPicker(picker);
    if (!productData) {
      console.warn('updateUnavailableStatesForVariantCard: No product data found');
      return;
    }

    const variantsList = productData?.variants || [];
    const optionsList = productData?.options || [];

    const selectedOptions = {};
    const checkedRadios = Array.from(picker.querySelectorAll('input[type="radio"]:checked'));
    const productId = picker.dataset.productId || '';

    checkedRadios.forEach((radio) => {
      if (!(radio instanceof HTMLInputElement)) return;
      const optionName = radio.name.replace(`-${productId}`, '');
      selectedOptions[optionName] = radio.value;
    });

    // Reset ALL unavailable states first
    const allLabels = Array.from(picker.querySelectorAll('label.sunice-variant-option__button-label'));
    allLabels.forEach((label) => {
      if (!(label instanceof HTMLElement)) return;
      
      // CRITICAL: Skip facet swatches - never modify them (desktop or mobile drawer)
      if (label.closest('.facets__item, .facets__panel, .facets--drawer, facets-form-component')) {
        return;
      }
      
      const radio = label.querySelector('input[type="radio"]');
      if (!(radio instanceof HTMLInputElement)) return;
      
      if (radio.dataset.optionAvailable !== 'false') {
        label.classList.remove('unavailable');
        label.classList.remove('variant-option__button-label--out-of-stock');
        label.removeAttribute('data-unavailable');
        label.removeAttribute('data-option-available');
        radio.removeAttribute('data-option-available');
        radio.disabled = false;
        radio.removeAttribute('disabled');
      }
    });

    // Check each option value to see if it's available with current selections
    const allRadios = Array.from(picker.querySelectorAll('input[type="radio"]'));
    allRadios.forEach((radio) => {
      if (!(radio instanceof HTMLInputElement)) return;
      const label = radio.closest('label');
      if (!(label instanceof HTMLElement)) return;
      
      // CRITICAL: Skip facet swatches - never modify them (desktop or mobile drawer)
      if (label.closest('.facets__item, .facets__panel, .facets--drawer, facets-form-component') || 
          radio.closest('.facets__item, .facets__panel, .facets--drawer, facets-form-component')) {
        return;
      }

      const isOptionInherentlyUnavailable = radio.dataset.optionAvailable === 'false';

      if (radio.checked) {
        if (isOptionInherentlyUnavailable) {
          label.classList.add('unavailable');
          label.classList.add('variant-option__button-label--out-of-stock');
          label.setAttribute('data-unavailable', 'true');
          label.setAttribute('data-option-available', 'false');
          radio.setAttribute('data-option-available', 'false');
        } else {
          label.classList.remove('unavailable');
          label.classList.remove('variant-option__button-label--out-of-stock');
          label.removeAttribute('data-unavailable');
          label.setAttribute('data-option-available', 'true');
          radio.setAttribute('data-option-available', 'true');
          radio.disabled = false;
          radio.removeAttribute('disabled');
        }
        return;
      }

      const optionName = radio.name.replace(`-${productId}`, '');
      const optionValue = radio.value;

      // Colour swatches: only mark unavailable if this colour has ZERO available variants (any size)
      if (label.classList.contains('color-swatch') && (optionName === 'Color' || optionName === 'Colour')) {
        const colorOption = optionsList.find(opt => opt?.name === 'Color' || opt?.name === 'Colour');
        if (colorOption) {
          const colorOptionPosition = colorOption.position - 1;
          const normalizeVal = (v) => !v ? '' : v.toString().toLowerCase().trim().replace(/\\\//g, '/');
          const normColor = normalizeVal(optionValue);
          const hasAnyForColor = variantsList.some((v) => {
            const variantColor = v.options?.[colorOptionPosition];
            return normalizeVal(variantColor) === normColor && v.available;
          });
          if (hasAnyForColor) {
            label.classList.remove('unavailable');
            label.classList.remove('variant-option__button-label--out-of-stock');
            label.removeAttribute('data-unavailable');
            label.setAttribute('data-option-available', 'true');
            radio.setAttribute('data-option-available', 'true');
            radio.disabled = false;
            radio.removeAttribute('disabled');
          } else {
            label.classList.add('unavailable');
            label.classList.add('variant-option__button-label--out-of-stock');
            label.setAttribute('data-unavailable', 'true');
            label.setAttribute('data-option-available', 'false');
            radio.setAttribute('data-option-available', 'false');
          }
        }
        return;
      }

      if (isOptionInherentlyUnavailable) {
        label.classList.add('unavailable');
        label.classList.add('variant-option__button-label--out-of-stock');
        label.setAttribute('data-unavailable', 'true');
        label.setAttribute('data-option-available', 'false');
        radio.setAttribute('data-option-available', 'false');
        return;
      }

      if (Object.keys(selectedOptions).length === 0) {
        if (radio.dataset.optionAvailable === 'false') {
          label.classList.add('unavailable');
          label.classList.add('variant-option__button-label--out-of-stock');
          label.setAttribute('data-unavailable', 'true');
          label.setAttribute('data-option-available', 'false');
          radio.setAttribute('data-option-available', 'false');
        } else {
          label.classList.remove('unavailable');
          label.classList.remove('variant-option__button-label--out-of-stock');
          label.removeAttribute('data-unavailable');
          label.setAttribute('data-option-available', 'true');
          radio.setAttribute('data-option-available', 'true');
          radio.disabled = false;
          radio.removeAttribute('disabled');
        }
        return;
      }

      const testOptions = { ...selectedOptions, [optionName]: optionValue };

      let variantExists = false;
      let hasAvailableVariant = false;
      
      for (const variant of variantsList) {
        let matches = true;

        for (const testOptionName in testOptions) {
          const option = optionsList.find((opt) => opt?.name === testOptionName);
          if (!option) {
            matches = false;
            break;
          }

          const optionPosition = option.position - 1;
          const expectedValue = testOptions[testOptionName];
          const variantValue = variant.options?.[optionPosition];

          if (!expectedValue) {
            matches = false;
            break;
          }

          const normalizedExpected = expectedValue.toString().toLowerCase().trim().replace(/\\\//g, '/');
          const normalizedVariant = variantValue ? variantValue.toString().toLowerCase().trim().replace(/\\\//g, '/') : '';

          if (!normalizedVariant || normalizedVariant !== normalizedExpected) {
            matches = false;
            break;
          }
        }

        if (matches) {
          variantExists = true;
          if (variant.available) {
            hasAvailableVariant = true;
          }
        }
      }

      const isUnavailable = !variantExists || (variantExists && !hasAvailableVariant);

      if (isUnavailable) {
        label.classList.add('unavailable');
        label.classList.add('variant-option__button-label--out-of-stock');
        label.setAttribute('data-unavailable', 'true');
        label.setAttribute('data-option-available', 'false');
        radio.setAttribute('data-option-available', 'false');
      } else {
        label.classList.remove('unavailable');
        label.classList.remove('variant-option__button-label--out-of-stock');
        label.removeAttribute('data-unavailable');
        label.setAttribute('data-option-available', 'true');
        radio.setAttribute('data-option-available', 'true');
        radio.disabled = false;
        radio.removeAttribute('disabled');
      }
    });
  }

  // Reorganize seasonal swatches for product card variant pickers (same logic as sunice-product-card.js)
  function reorganizeSeasonalSwatchesForPicker(picker) {
    if (!(picker instanceof HTMLElement)) return;
    
    // Find all seasonal swatches in this picker
    const allSeasonalSwatches = Array.from(picker.querySelectorAll('label[data-seasonal="true"], .seasonal-variant-hidden'));
    
    if (allSeasonalSwatches.length === 0) return;
    
    // Find or create the seasonal fieldset
    let seasonalFieldset = picker.querySelector('.seasonal-variant-section');
    const coreFieldset = Array.from(picker.querySelectorAll('.variant-option--buttons')).find(fs => 
      !fs.classList.contains('seasonal-variant-section') && 
      fs.querySelector('.color-swatch')
    );
    
    if (!seasonalFieldset) {
      // Create a new fieldset for Seasonal swatches
      seasonalFieldset = document.createElement('fieldset');
      seasonalFieldset.className = 'variant-option variant-option--buttons seasonal-variant-section';
      
      // Create legend for Seasonal with selected value span always present
      const seasonalLegend = document.createElement('legend');
      seasonalLegend.textContent = 'Seasonal:';
      const seasonalValueSpan = document.createElement('span');
      seasonalValueSpan.className = 'variant-option__selected-value';
      seasonalLegend.appendChild(seasonalValueSpan);
      seasonalFieldset.appendChild(seasonalLegend);
      
      // Create buttons container
      const seasonalButtons = document.createElement('div');
      seasonalButtons.className = 'variant-option__buttons';
      seasonalFieldset.appendChild(seasonalButtons);
      
      // Insert the new Seasonal fieldset BEFORE the Core fieldset (or at the beginning of the picker)
      if (coreFieldset) {
        coreFieldset.parentNode.insertBefore(seasonalFieldset, coreFieldset);
      } else {
        picker.insertBefore(seasonalFieldset, picker.firstChild);
      }
    }
    
    const seasonalButtons = seasonalFieldset.querySelector('.variant-option__buttons');
    if (!seasonalButtons) return;
    
    // Collect all seasonal swatches that need to be moved (not already in seasonal fieldset)
    const swatchesToMove = allSeasonalSwatches.filter(swatch => {
      // Don't move if already in seasonal fieldset
      if (seasonalFieldset.contains(swatch)) return false;
      
      // Remove the temporary hide class
      swatch.classList.remove('seasonal-variant-hidden');
      
      // Ensure data-seasonal attribute is set
      swatch.setAttribute('data-seasonal', 'true');
      const input = swatch.querySelector('input[type="radio"]');
      if (input) {
        input.setAttribute('data-seasonal', 'true');
      }
      
      return true;
    });
    
    // Move swatches that aren't already in the seasonal fieldset
    if (swatchesToMove.length > 0) {
      swatchesToMove.forEach(swatch => {
        // Ensure swatch is always visible, even if out of stock
        swatch.style.display = '';
        swatch.removeAttribute('hidden');
        
        seasonalButtons.appendChild(swatch);
      });
    }
    
    // Ensure all seasonal swatches in the fieldset remain visible (even if out of stock)
    // AND especially if they are active/selected
    const allSeasonalInFieldset = seasonalButtons.querySelectorAll('label');
    allSeasonalInFieldset.forEach(swatch => {
      const input = swatch.querySelector('input[type="radio"]');
      const isActive = input?.checked || swatch.hasAttribute('data-active') || swatch.classList.contains('active');
      
      swatch.style.display = '';
      swatch.removeAttribute('hidden');
      swatch.setAttribute('data-seasonal', 'true');
      
      // If active, ensure it's always visible and clickable
      if (isActive) {
        swatch.style.visibility = 'visible';
        swatch.style.opacity = '1';
        swatch.style.pointerEvents = 'auto';
        swatch.classList.remove('unavailable');
        swatch.removeAttribute('data-unavailable');
        if (input) {
          input.disabled = false;
        }
      }
    });
    
    // Clear all color swatch active states, then restore only for the checked color swatch
    clearAllColorSwatchActiveStates(picker);
    const checkedColorRadio = picker.querySelector('label.color-swatch input[type="radio"]:checked');
    if (checkedColorRadio) {
      const checkedLabel = checkedColorRadio.closest('label.color-swatch');
      if (checkedLabel) {
        checkedLabel.classList.add('active');
        checkedLabel.setAttribute('data-active', 'true');
      }
    }
    
    // Update legends for seasonal and core
    updateProductCardLegends(picker);
  }

  // Update legends for product card variant pickers (same logic as sunice-product-card.js)
  function updateProductCardLegends(picker) {
    if (!(picker instanceof HTMLElement)) return;
    
    const seasonalFieldset = picker.querySelector('.seasonal-variant-section');
    const coreFieldset = Array.from(picker.querySelectorAll('.variant-option--buttons')).find(fs => 
      !fs.classList.contains('seasonal-variant-section') && 
      fs.querySelector('.color-swatch')
    );
    
    // Update Seasonal legend
    if (seasonalFieldset) {
      const seasonalLegend = seasonalFieldset.querySelector('legend');
      if (seasonalLegend) {
        let seasonalValueSpan = seasonalLegend.querySelector('.variant-option__selected-value');
        if (!seasonalValueSpan) {
          seasonalValueSpan = document.createElement('span');
          seasonalValueSpan.className = 'variant-option__selected-value';
          seasonalLegend.appendChild(seasonalValueSpan);
        }
        
        const selectedSeasonal = seasonalFieldset.querySelector('input[type="radio"]:checked, input[type="radio"][checked]');
        if (selectedSeasonal) {
          seasonalValueSpan.textContent = selectedSeasonal.value;
        } else {
          const firstSeasonalInput = seasonalFieldset.querySelector('input[type="radio"]');
          if (firstSeasonalInput) {
            seasonalValueSpan.textContent = firstSeasonalInput.value;
          } else {
            seasonalValueSpan.textContent = '';
          }
        }
      }
    }
    
    // Update Core legend
    if (coreFieldset) {
      const coreLegend = coreFieldset.querySelector('legend');
      if (coreLegend) {
        // Clean up legend text - ensure it only contains "Core:" (or whatever the base text is)
        const baseText = 'Core:';
        // Remove any existing text content that includes values after "Core:"
        if (coreLegend.textContent && coreLegend.textContent.trim() !== baseText && !coreLegend.textContent.trim().startsWith(baseText + '<')) {
          // Check if there's already a span
          const existingSpan = coreLegend.querySelector('.variant-option__selected-value');
          if (!existingSpan) {
            // Clean up and set to just base text
            coreLegend.textContent = baseText;
          }
        }
        
        let coreValueSpan = coreLegend.querySelector('.variant-option__selected-value');
        if (!coreValueSpan) {
          coreValueSpan = document.createElement('span');
          coreValueSpan.className = 'variant-option__selected-value';
          // Ensure legend only contains base text before adding span
          if (coreLegend.textContent.trim() !== baseText) {
            coreLegend.textContent = baseText;
          }
          coreLegend.appendChild(coreValueSpan);
        }
        
        const selectedCore = coreFieldset.querySelector('input[type="radio"]:checked, input[type="radio"][checked]');
        if (selectedCore) {
          coreValueSpan.textContent = selectedCore.value;
        } else {
          const firstCoreInput = coreFieldset.querySelector('input[type="radio"]');
          if (firstCoreInput) {
            coreValueSpan.textContent = firstCoreInput.value;
          } else {
            coreValueSpan.textContent = '';
          }
        }
      }
    }
    
    // Update Size legend
    const sizeFieldset = Array.from(picker.querySelectorAll('.variant-option--buttons')).find(fs => {
      const legend = fs.querySelector('legend');
      return legend && (legend.textContent.includes('Size:') || legend.textContent.includes('size:'));
    });
    
    if (sizeFieldset) {
      const sizeLegend = sizeFieldset.querySelector('legend');
      if (sizeLegend) {
        let sizeValueSpan = sizeLegend.querySelector('.variant-option__selected-value');
        if (!sizeValueSpan) {
          sizeValueSpan = document.createElement('span');
          sizeValueSpan.className = 'variant-option__selected-value';
          sizeLegend.appendChild(sizeValueSpan);
        }
        
        const selectedSize = sizeFieldset.querySelector('input[type="radio"]:checked, input[type="radio"][checked]');
        if (selectedSize) {
          const sizeLabel = selectedSize.closest('label');
          if (sizeLabel) {
            const sizeText = sizeLabel.querySelector('.variant-option__button-label__text')?.textContent?.trim() || 
                            sizeLabel.textContent.trim() || 
                            selectedSize.value;
            sizeValueSpan.textContent = sizeText;
          } else {
            sizeValueSpan.textContent = selectedSize.value;
          }
        } else {
          const firstSizeInput = sizeFieldset.querySelector('input[type="radio"]');
          if (firstSizeInput) {
            const firstLabel = firstSizeInput.closest('label');
            if (firstLabel) {
              const sizeText = firstLabel.querySelector('.variant-option__button-label__text')?.textContent?.trim() || 
                              firstLabel.textContent.trim() || 
                              firstSizeInput.value;
              sizeValueSpan.textContent = sizeText;
            } else {
              sizeValueSpan.textContent = firstSizeInput.value;
            }
          } else {
            sizeValueSpan.textContent = '';
          }
        }
      }
    }
  }
})();



